import { motion } from "motion/react";
import { Link } from "react-router";
import { Building2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

export function GetInvolved() {
  const opportunities = [
    {
      icon: Building2,
      title: "Volunteer",
      description:
        "Join our on-ground verification teams, help with project monitoring, or contribute your skills remotely.",
      color: "bg-primary",
      cta: "Explore Opportunities",
      link: "#volunteer",
    },
    {
      icon: Building2,
      title: "CSR Partnership",
      description:
        "Align your corporate social responsibility goals with our verified projects for measurable social impact.",
      color: "bg-accent",
      cta: "Partner With Us",
      link: "#csr",
    },
    {
      icon: Building2,
      title: "Join a Campaign",
      description:
        "Support our fundraising campaigns, spread awareness, or organize events in your community.",
      color: "bg-secondary",
      cta: "View Campaigns",
      link: "#campaigns",
    },
    {
      icon: Building2,
      title: "For Educators",
      description:
        "Bring sustainable development education to your classroom with our curriculum resources.",
      color: "bg-primary",
      cta: "Teaching Resources",
      link: "#educators",
    },
    {
      icon: Building2,
      title: "Student Programs",
      description:
        "Internships, research opportunities, and campus ambassador programs for students passionate about impact.",
      color: "bg-accent",
      cta: "Learn More",
      link: "#students",
    },
    {
      icon: Building2,
      title: "Write for Us",
      description:
        "Share your expertise in sustainability, tourism, or community development through our blog.",
      color: "bg-secondary",
      cta: "Submit Your Story",
      link: "#write",
    },
  ];

  const campaigns = [
    {
      title: "Empowering Young Athletes",
      description:
        "Empowering Girls Football Team at Sanik School supporting young girls by providing complete sports dress for their upcoming football tournament, while promoting confidence, teamwork, and awareness of girls in sports.",
      image:"/Football%20Girl.png",
    },
    {
      title: "Empowering the Next Generation of Innovators in Himachal Pradesh",
      description:
        "Empowering India Coder kids – Skills for Future Initiative Empowering rural kids with coding, digital literacy, and future-ready skills.",
      image:
      "https://images.unsplash.com/photo-1762614351385-0b3002530c1c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlY28lMjB0b3VyaXNtJTIwbW91bnRhaW4lMjB2aWxsYWdlJTIwbGFuZHNjYXBlfGVufDF8fHx8MTc3NDg3MDQ3Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl mb-6">Get Involved</h1>
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
              There are many ways to contribute to meaningful, sustainable change. Find the way that aligns with you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {opportunities.map((opportunity, index) => (
              <motion.div
                key={opportunity.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow group">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div
                      className={`w-16 h-16 rounded-lg ${opportunity.color} text-white flex items-center justify-center mb-6`}
                    >
                      <opportunity.icon size={32} />
                    </div>
                    <h3 className="text-2xl mb-3">{opportunity.title}</h3>
                    <p className="text-muted-foreground mb-6 flex-1">
                      {opportunity.description}
                    </p>
                    <a href="#get-involved-form" className="w-full">
                      <Button variant="outline" className="w-full">
                        {opportunity.cta}
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Campaigns */}
      <section className="py-24 bg-white" id="campaigns">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl mb-4">Active Campaigns</h2>
            <p className="text-xl text-muted-foreground">
              Join our focused campaigns for specific causes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {campaigns.map((campaign, index) => (
              <motion.div
                key={campaign.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={campaign.image}
                      alt={campaign.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-8">
                    <h3 className="text-2xl mb-3">{campaign.title}</h3>
                    <p className="text-muted-foreground mb-6">
                      {campaign.description}
                    </p>
                    <Link to="/donate">
                      <Button className="w-full">Support This Campaign</Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Partnership */}
      <section className="py-24" id="csr">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1769739576456-0aefcff3f4b9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBvZmZpY2UlMjB0ZWFtd29yayUyMHByb2Zlc3Npb25hbHxlbnwxfHx8fDE3NzQ4NzA0Nzh8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="CSR Partnership"
                className="rounded-lg shadow-xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl mb-6">
                Corporate Social Responsibility
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Partner with Immerse Foundation to fulfill your CSR mandate with
                transparent, measurable impact projects.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    ✓
                  </div>
                  <span>Verified, high-impact projects aligned with SDGs</span>
                </li>
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    ✓
                  </div>
                  <span>Detailed impact reports and documentation</span>
                </li>
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    ✓
                  </div>
                  <span>Employee engagement opportunities</span>
                </li>
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    ✓
                  </div>
                  <span>Compliance with CSR regulations</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button size="lg">Schedule a Meeting</Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      {/* <section className="py-24 bg-white" id="volunteer">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl mb-4">Volunteer With Us</h2>
            <p className="text-xl text-muted-foreground">
              Use your skills to support sustainable development
            </p>
          </motion.div>

          <Card>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl mb-3">On-Ground Opportunities</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Project site visits and verification</li>
                    <li>• Community engagement activities</li>
                    <li>• Photography and documentation</li>
                    <li>• Event organization and management</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl mb-3">Remote Opportunities</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Content writing and storytelling</li>
                    <li>• Graphic design and video editing</li>
                    <li>• Social media management</li>
                    <li>• Data analysis and reporting</li>
                  </ul>
                </div>
              </div>
              <Button size="lg" className="w-full">
                Apply to Volunteer
              </Button>
            </CardContent>
          </Card>
        </div>
      </section> */}

      {/* Get Involved Form */}
      <section className="py-24" id="get-involved-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl mb-4">Ways to Get Involved</h2>
            <p className="text-xl text-muted-foreground">
              Tell us about yourself and how you'd like to contribute
            </p>
          </motion.div>

          <Card>
            <CardContent className="p-8">
              <form className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Organisation / Institution */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Organisation / Institution
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your organisation or institution"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Area of Interest / Contribution */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Area of Interest / Contribution
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                    <option value="">Select an area of interest...</option>
                    <option value="volunteer">Volunteer</option>
                    <option value="csr">CSR Partnership</option>
                    <option value="campaign">Join a Campaign</option>
                    <option value="educator">For Educators</option>
                    <option value="student">Student Programs</option>
                    <option value="writer">Write for Us</option>
                  </select>
                </div>

                {/* Tell us more */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tell us more
                  </label>
                  <textarea
                    placeholder="Share more details about your interest or how you'd like to contribute..."
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button size="lg" className="w-full">
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
